import { A11yModule } from '@angular/cdk/a11y';
import { DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { PendoService } from '../../../shared/services/analytics/pendo.service';

export interface IUpdateLimitSuggestion {
  label: string;
  value: string;
}

/** How the limit was last set while the popup was open. */
type UpdateLimitInputMethod = 'suggestion' | 'typed' | 'remove-button';

/**
 * Popup editor for a Rule's record update limit, opened from the Rule modal header.
 * Edits the shared `control` directly: a positive whole number is the limit, `null` means no limit.
 */
@Component({
  selector: 'app-update-limit-popup',
  standalone: true,
  templateUrl: './update-limit-popup.component.html',
  styleUrls: ['./update-limit-popup.component.scss'],
  imports: [
    A11yModule,
    DecimalPipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatTooltip,
    ReactiveFormsModule
  ]
})
export class UpdateLimitPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('limitInput') private limitInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger: MatAutocompleteTrigger;

  @Input({ required: true }) control: FormControl<number | null>;
  /** The Rule's entity, only used to describe the limit change in analytics. */
  @Input() ruleEntity: string | undefined;

  @Output() readonly closed = new EventEmitter<void>();

  readonly suggestions: IUpdateLimitSuggestion[] = [
    { label: 'No limit', value: '' },
    ...[50, 100, 500, 1000, 2000, 5000].map(limit => ({ label: UpdateLimitPopupComponent.formatLimit(limit), value: String(limit) }))
  ];

  /** The text in the field. Not the control itself, as the field also holds in-progress and formatted text. */
  readonly fieldControl = new FormControl('', { nonNullable: true });

  private limitWhenOpened: number | null = null;
  private inputMethod: UpdateLimitInputMethod = 'typed';

  constructor(private pendoService: PendoService) {
    this.fieldControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(text => this.onFieldTextChanged(text));
  }

  static formatLimit(limit: number): string {
    return limit.toLocaleString('en-GB');
  }

  /** Empty, zero or invalid text means no limit. */
  static parseLimit(text: string): number | null {
    const limit = Number(text);
    return Number.isSafeInteger(limit) && limit > 0 ? limit : null;
  }

  ngOnInit(): void {
    this.limitWhenOpened = this.control.value ?? null;
    this.fieldControl.setValue(this.control.value ? String(this.control.value) : '', { emitEvent: false });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusFieldWithoutOpeningSuggestions());
  }

  onFocus(): void {
    this.setFieldText(this.control.value ? String(this.control.value) : '');
    this.limitInput.nativeElement.select();
  }

  onBlur(): void {
    this.setFieldText(this.control.value ? UpdateLimitPopupComponent.formatLimit(this.control.value) : '');
  }

  onBeforeInput(event: InputEvent): void {
    if (event.data && /\D/.test(event.data)) {
      event.preventDefault();
    }
  }

  togglePanel(): void {
    if (this.autocompleteTrigger.panelOpen) {
      this.autocompleteTrigger.closePanel();
      return;
    }
    this.limitInput.nativeElement.focus({ preventScroll: true })  ;
    this.autocompleteTrigger.openPanel();
  }

  /** Runs after the autocomplete has written the picked suggestion into the field, so it overrides `typed`. */
  onSuggestionSelected(): void {
    this.inputMethod = 'suggestion';
  }

  removeLimit(): void {
    this.setFieldText('');
    this.applyLimit(null);
    this.inputMethod = 'remove-button';
    this.focusFieldWithoutOpeningSuggestions();
  }

  close(): void {
    this.closed.emit();
  }

  onEscape(event: Event): void {
    if (this.autocompleteTrigger.panelOpen) {
      return;
    }
    event.stopPropagation();
    this.close();
  }

  ngOnDestroy(): void {
    this.trackLimitChange();
  }

  /**
   * One event when the popup closes, whichever way it closes, if the limit differs from when it opened.
   * Values the user tried along the way are not sent. It records what was chosen, not whether the Rule was saved afterwards.
   */
  private trackLimitChange(): void {
    const previousLimit = this.limitWhenOpened;
    const limit = this.control.value ?? null;
    if (limit === previousLimit) {
      return;
    }
    this.pendoService.recordEvent('DataRules | Update Limit Changed', {
      ...(limit !== null ? { limit } : {}),
      ...(previousLimit !== null ? { previousLimit } : {}),
      change: limit === null ? 'removed' : previousLimit === null ? 'set' : 'changed',
      inputMethod: this.inputMethod,
      isSuggestedValue: limit !== null && this.suggestions.some(suggestion => suggestion.value === String(limit)),
      ruleEntity: this.pendoService.truncateForPendo(this.ruleEntity ?? 'unknown')
    });
  }

  private onFieldTextChanged(text: string): void {
    this.inputMethod = 'typed';
    const digits = text.replace(/\D/g, '');
    if (digits !== text) {
      this.setFieldText(digits);
    }
    this.applyLimit(UpdateLimitPopupComponent.parseLimit(digits));
  }

  private applyLimit(limit: number | null): void {
    if (this.control.value === limit) {
      return;
    }
    this.control.setValue(limit);
    this.control.markAsDirty();
  }

  /** Suggestions open on focus, so keep them shut when focus is moved here for the user (popup opened, REMOVE LIMIT clicked). */
  private focusFieldWithoutOpeningSuggestions(): void {
    this.autocompleteTrigger.autocompleteDisabled = true;
    this.limitInput.nativeElement.focus({ preventScroll: true });
    this.limitInput.nativeElement.select();
    this.autocompleteTrigger.autocompleteDisabled = false;
  }

  /** Writes straight to the input so it takes effect at once, as the autocomplete writes its own value on the next cycle. */
  private setFieldText(text: string): void {
    this.fieldControl.setValue(text, { emitEvent: false, emitModelToViewChange: false });
    this.limitInput.nativeElement.value = text;
  }
}
